import React, { useCallback, useEffect, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { toast } from '@cotera/client/app/components/ui';
import { CocoEvent, useAnalytics } from '@cotera/client/app/etc';
import type { ResourceShareLinkAudience } from '@cotera/client/app/etc/api/typed-client';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import {
  SharePermissionSelect,
  type SharePermission,
} from './share-permission-select';
import { VIEW_UNAVAILABLE_HINT, type ShareTarget } from './share-targets';
import { ShareSwitch } from './share-switch';
import { apiErrorMessage, useActiveShareLinks } from './use-share-access';

export type LinkExpiryDays = 7 | 30;

/**
 * What the next link made will do. Lives with the panel rather than either view
 * so a change made in Share settings is what the main view's Copy link uses.
 */
export type LinkSettings = {
  /** `copy`: each person who opens it gets their own copy, not access. */
  mode: 'access' | 'copy';
  audience: ResourceShareLinkAudience;
  permission: SharePermission;
  expiresInDays: LinkExpiryDays;
};

/**
 * How long a link works, set by who it works for rather than picked: a link
 * that only works inside the org can live longer than one that works for
 * anyone signed in to Coco.
 */
export const LINK_EXPIRY_DAYS: Record<
  ResourceShareLinkAudience,
  LinkExpiryDays
> = { org: 30, anyone: 7 };
const COPIED_FEEDBACK_MS = 2_000;

/**
 * Google Workspace's defaults: org-only, a week, and View wherever the
 * resource has a real read-only mode.
 */
export const defaultLinkSettings = (allowView: boolean): LinkSettings => ({
  mode: 'access',
  audience: 'org',
  permission: allowView ? 'view' : 'edit',
  expiresInDays: LINK_EXPIRY_DAYS.org,
});

/**
 * Who a link works for follows from what it hands out, so it isn't a choice.
 * Access stays inside the org (someone outside is invited by email instead,
 * with a link only their address opens); a copy leaves the original private,
 * so it can go to anyone on Coco, which is how work reaches another org.
 */
export const withMode = (
  settings: LinkSettings,
  mode: LinkSettings['mode']
): LinkSettings => {
  const audience: ResourceShareLinkAudience =
    mode === 'copy' ? 'anyone' : 'org';
  return {
    ...settings,
    mode,
    audience,
    expiresInDays: LINK_EXPIRY_DAYS[audience],
  };
};

/** Who a link works for, as the start of a sentence: "Anyone at Cotera". */
export const audienceLabel = (
  audience: ResourceShareLinkAudience,
  orgName: string
): string => (audience === 'org' ? `Anyone at ${orgName}` : 'Anyone on Coco');

export const linkAudienceLabel = (
  settings: LinkSettings,
  orgName: string
): string => audienceLabel(settings.audience, orgName);

export const effectivePermission = (
  settings: LinkSettings,
  allowView: boolean
): SharePermission => (allowView ? settings.permission : 'edit');

/** The absolute address to hand out, from the server's path or the raw token. */
export function buildShareUrl(urlOrPath: string, token: string): string {
  if (urlOrPath.startsWith('http://') || urlOrPath.startsWith('https://')) {
    return urlOrPath;
  }
  if (urlOrPath === '') {
    return `${window.location.origin}/share/${token}`;
  }
  return `${window.location.origin}${
    urlOrPath.startsWith('/') ? '' : '/'
  }${urlOrPath}`;
}

/** Makes a link with the current settings and puts it on the clipboard. */
export function useCopyShareLink(targets: ShareTarget[], allowView: boolean) {
  const client = useTenantedClient();
  const analytics = useAnalytics();
  const queryClient = useQueryClient();
  const orgId = useWhoami((s) => s.org.id);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) {
      return;
    }
    const timeout = window.setTimeout(
      () => setCopied(false),
      COPIED_FEEDBACK_MS
    );
    return () => window.clearTimeout(timeout);
  }, [copied]);

  const copy = useCallback(
    /** Resolves to the link once it is on the clipboard, or null. */
    async (settings: LinkSettings): Promise<string | null> => {
      const permission = effectivePermission(settings, allowView);
      setCreating(true);
      const result = await client.resource.createShareLink({
        targets: targets.map((t) => ({ type: t.type, id: t.id })),
        shareMode: settings.mode,
        expiresInDays: settings.expiresInDays,
        audience: settings.audience,
        ...(settings.mode === 'access' && {
          grantTargetType: 'user' as const,
          permission,
        }),
      });
      setCreating(false);
      if (result.isErr()) {
        toast.error(apiErrorMessage(result.error, "Couldn't create a link"));
        return null;
      }
      // Active links lists it straight away, not after the cache goes stale.
      void Promise.all(
        targets.map((t) =>
          queryClient.invalidateQueries({
            queryKey: queryKeys.resource.shareLinks({
              orgId,
              type: t.type,
              id: t.id,
            }),
          })
        )
      );
      const url = buildShareUrl(result.value.url, result.value.token);
      await navigator.clipboard.writeText(url);
      setCopied(true);
      analytics.track(CocoEvent.ShareResourceLinkCreated, {
        targetCount: targets.length,
        shareMode: settings.mode,
        permission: settings.mode === 'access' ? permission : undefined,
        audience: settings.audience,
        expiresInDays: settings.expiresInDays,
      });
      return url;
    },
    [allowView, analytics, client.resource, orgId, queryClient, targets]
  );

  /** Puts an address that needs no new link on the clipboard. */
  const copyAddress = useCallback(async (url: string) => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
  }, []);

  return { copy, copyAddress, creating, copied };
}

/**
 * The link part of share settings: whether it hands out copies, and what
 * people who open it can do. Who it works for follows from that (see
 * {@link withMode}).
 */
export const ShareSettingsView: React.FC<{
  settings: LinkSettings;
  onChange: (next: LinkSettings) => void;
  allowView: boolean;
  subject: ShareTarget;
  /** Off in bulk, where "the links on this" has no single subject. */
  showActiveLinks: boolean;
  onOpenActiveLinks: () => void;
  /**
   * Everyone in the org can already edit, so an access link could give no
   * one anything more and its level isn't asked for.
   */
  orgCanEdit: boolean;
}> = ({
  settings,
  onChange,
  allowView,
  subject,
  showActiveLinks,
  onOpenActiveLinks,
  orgCanEdit,
}) => {
  const orgName = useWhoami((s) => s.org.name);
  const linksQuery = useActiveShareLinks(subject, showActiveLinks);
  const activeLinkCount = linksQuery.data?.length ?? 0;
  const set = (patch: Partial<LinkSettings>) =>
    onChange({ ...settings, ...patch });

  return (
    <div className="flex flex-col gap-5">
      <SettingRow
        title="Link gives a copy"
        hint={`People get their own copy to change, and your original stays private. Works for anyone on Coco for ${LINK_EXPIRY_DAYS.anyone} days.`}
        control={
          <ShareSwitch
            aria-label="Link gives a copy"
            checked={settings.mode === 'copy'}
            onCheckedChange={(checked) =>
              onChange(withMode(settings, checked ? 'copy' : 'access'))
            }
          />
        }
      />

      {settings.mode === 'access' && orgCanEdit ? (
        <p className="text-xs text-muted-foreground">
          Everyone at {orgName} can already edit, so Copy link copies the page
          address.
        </p>
      ) : settings.mode === 'access' ? (
        <SettingRow
          title="People with the link can"
          hint={
            allowView
              ? 'Editors can change it; viewers can only read.'
              : VIEW_UNAVAILABLE_HINT
          }
          control={
            <SharePermissionSelect
              value={effectivePermission(settings, allowView)}
              onChange={(permission) => set({ permission })}
              allowView={allowView}
              aria-label="People with the link can"
              className="h-8 w-32 justify-between border border-solid border-input px-3 text-sm shadow-xs"
            />
          }
        />
      ) : null}

      {/* Always here, so turning links off has one place to find it. */}
      {showActiveLinks ? (
        <button
          type="button"
          onClick={onOpenActiveLinks}
          className="-mx-1 flex items-center gap-3 rounded-md px-1 py-1.5 text-left hover:bg-muted"
        >
          <span className="min-w-0 flex-1">
            <span className="block text-sm">Active links</span>
            <span className="block text-xs text-muted-foreground">
              {linksQuery.isPending
                ? 'Links still open, and a way to turn them off.'
                : activeLinkCount === 0
                ? 'No links are open right now.'
                : `${activeLinkCount} ${
                    activeLinkCount === 1 ? 'link' : 'links'
                  } still open. Review or turn them off.`}
            </span>
          </span>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </button>
      ) : null}
    </div>
  );
};

const SettingRow: React.FC<{
  title: string;
  hint: string;
  control: React.ReactNode;
}> = ({ title, hint, control }) => (
  <div className="flex items-center gap-4">
    <div className="min-w-0 flex-1">
      <p className="text-sm">{title}</p>
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
    <div className="shrink-0">{control}</div>
  </div>
);
